import {
  ChevronDown,
  ChevronRight,
  Code,
  Cpu,
  FileCode,
  FileText,
  FolderOpen,
  GitBranch,
  Globe,
  Search,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import { Markdown } from "@/components/shared/Markdown";
import type { ReasoningStep } from "@/types";

interface ReasoningTraceProps {
  steps: ReasoningStep[];
  /** Provider-written summary of the model's reasoning; shown above the tool calls. */
  summary?: string | null;
}

/** Icon and accent color per tool name. */
const TOOL_STYLES: Record<string, { icon: typeof Wrench; color: string; bg: string; border: string; label: string }> = {
  web_search: {
    icon: Globe,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    label: "Web Search",
  },
  file_search: {
    icon: FileText,
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    label: "File Search",
  },
  dynamic_session: {
    icon: Code,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    label: "Code Execution",
  },
  fetch_sharepoint_document: {
    icon: FolderOpen,
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    label: "SharePoint Document",
  },
  search_sharepoint: {
    icon: Search,
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    label: "SharePoint Search",
  },
  list_sharepoint_sites: {
    icon: FolderOpen,
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    label: "SharePoint Sites",
  },
  delegate_to_agent: {
    icon: GitBranch,
    color: "text-cyan-400",
    bg: "bg-cyan-500/10",
    border: "border-cyan-500/20",
    label: "Delegate to Agent",
  },
  create_artifact: {
    icon: FileText,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    label: "Create Artifact",
  },
  generate_image: {
    icon: Cpu,
    color: "text-pink-400",
    bg: "bg-pink-500/10",
    border: "border-pink-500/20",
    label: "Generate Image",
  },
  generate_html: {
    icon: FileCode,
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    label: "Generate HTML",
  },
};

const DEFAULT_STYLE = {
  icon: Wrench,
  color: "text-content-tertiary",
  bg: "bg-surface-tertiary",
  border: "border-border-secondary",
  label: "Tool",
};

/**
 * Humanize a raw tool name for display.
 *
 * - MCP tools: strip the `mcp_{id}_` prefix and title-case the remainder
 *   e.g. "mcp_24_resolve-library-id" → "Resolve Library Id"
 * - API endpoint tools: replace underscores with spaces
 *   e.g. "Weather_Results_Tool" → "Weather Results Tool"
 * - Known tools: use the TOOL_STYLES label
 */
function humanizeToolName(raw: string): string {
  // Strip mcp_{id}_ prefix
  const mcpMatch = raw.match(/^mcp_\d+_(.+)$/);
  const base = mcpMatch ? mcpMatch[1] : raw;

  // Replace hyphens and underscores with spaces, then title-case
  return base.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function getToolStyle(toolName: string) {
  if (TOOL_STYLES[toolName]) return TOOL_STYLES[toolName];

  // MCP tools get a distinctive style
  if (toolName.startsWith("mcp_")) {
    return {
      icon: Globe,
      color: "text-teal-400",
      bg: "bg-teal-500/10",
      border: "border-teal-500/20",
      label: humanizeToolName(toolName),
    };
  }

  return { ...DEFAULT_STYLE, label: humanizeToolName(toolName) };
}

/**
 * Formats tool arguments JSON for display.
 * Shows key=value pairs inline for simple args, or truncated JSON for complex ones.
 */
function formatArgs(argsJson: string | Record<string, unknown> | undefined): string | null {
  if (!argsJson) return null;
  try {
    // Defensive: if argsJson is already an object (e.g. from streaming),
    // use it directly instead of parsing.
    const args = typeof argsJson === "object" ? argsJson : JSON.parse(argsJson);
    const entries = Object.entries(args);
    if (entries.length === 0) return null;
    // For simple args (1-3 keys with short values), show inline
    if (entries.length <= 3) {
      const parts = entries.map(([k, v]) => {
        const val = typeof v === "string" ? v : JSON.stringify(v);
        const display = val.length > 60 ? `${val.slice(0, 57)}...` : val;
        return `${k}: ${display}`;
      });
      return parts.join(", ");
    }
    // For complex args, show count
    return `${entries.length} parameters`;
  } catch {
    const fallback = typeof argsJson === "string" ? argsJson : JSON.stringify(argsJson);
    return fallback.length > 80 ? `${fallback.slice(0, 77)}...` : fallback;
  }
}

/**
 * Truncates tool result content for display.
 */
function truncateResult(content: string | undefined, maxLen = 300): string | null {
  if (!content) return null;
  if (content.length <= maxLen) return content;
  return `${content.slice(0, maxLen - 3)}...`;
}

/**
 * Collapsible "Show reasoning" panel that displays the intermediate tool
 * calls and results for an assistant message. Renders above the sources
 * panel and action buttons.
 */
export function ReasoningTrace({ steps, summary }: Readonly<ReasoningTraceProps>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const stepCount = steps.length;
  if (stepCount === 0 && !summary) return null;

  const toggleStep = (index: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="mt-3 rounded-lg border border-border-secondary bg-surface-secondary">
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-content-tertiary hover:text-content-secondary transition cursor-pointer"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        )}
        <Cpu className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">
          Show reasoning
          {stepCount > 0 && ` (${stepCount} tool${stepCount === 1 ? " call" : " calls"})`}
        </span>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-1.5">
          {summary && (
            <div className="px-2.5 py-2 rounded-lg bg-surface-tertiary border border-border-secondary">
              <Markdown content={summary} className="prose-sm text-xs text-content-secondary" />
            </div>
          )}
          {steps.map((step, i) => {
            const style = getToolStyle(step.toolName);
            const Icon = style.icon;
            const argsDisplay = formatArgs(step.toolArgs);
            const isStepExpanded = expandedSteps.has(i);
            const resultPreview = truncateResult(step.resultContent);

            return (
              <div key={step.toolCallId || i} className={`rounded-lg ${style.bg} border ${style.border}`}>
                {/* Step header */}
                <button
                  onClick={() => toggleStep(i)}
                  className="w-full flex items-start gap-2 px-2.5 py-1.5 text-left cursor-pointer"
                >
                  {(() => {
                    if (step.resultContent) {
                      return isStepExpanded ? (
                        <ChevronDown className="h-3 w-3 mt-0.5 shrink-0 text-content-quaternary" />
                      ) : (
                        <ChevronRight className="h-3 w-3 mt-0.5 shrink-0 text-content-quaternary" />
                      );
                    }
                    return <div className="w-3 h-3 mt-0.5 shrink-0" />;
                  })()}
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${style.color}`} />
                  <div className="min-w-0 flex-1">
                    <span className={`text-xs font-medium ${style.color}`}>{style.label}</span>
                    {argsDisplay && <span className="text-xs text-content-quaternary ml-2">{argsDisplay}</span>}
                  </div>
                </button>

                {/* Expanded result content */}
                {isStepExpanded && resultPreview && (
                  <div className="px-2.5 pb-2 ml-8">
                    <pre className="text-[11px] text-content-tertiary whitespace-pre-wrap wrap-break-word font-mono leading-relaxed max-h-60 overflow-y-auto bg-surface-primary/50 rounded-sm p-2">
                      {resultPreview}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
